import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { SKILL_ENTRYPOINT } from "encamp-shared/src/s3/skills/paths";
import { useMemo, useRef, useState } from "react";

import { prepareDraftSkillFile } from "./draftSkillFiles";
import { movedPath } from "./skillFilePaths";
import {
  DELETE_SKILL_FILE_MUTATION,
  SKILL_FILE_CONTENT_QUERY,
  SKILL_FILES_QUERY,
  WRITE_SKILL_FILE_MUTATION,
} from "./skillQueries";

/** Where the file tree editor reads and writes a skill's files. */
export type SkillFileStore = {
  loading: boolean;
  paths: string[];
  /** Sync for files already in memory, so opening them never flashes a skeleton. */
  read: (path: string) => string | Promise<string>;
  write: (path: string, content: string, options: { create: boolean }) => Promise<void>;
  remove: (path: string) => Promise<void>;
  /** Move a file, or a folder and everything in it. */
  move: (from: string, to: string) => Promise<void>;
  /** Toast after "Save file"; says where the text went. */
  savedMessage: string;
  /** Files held only in this browser, uploaded when the skill is created; null on the server. */
  localFiles: Record<string, string> | null;
};

const INITIAL_LOCAL_FILES: Record<string, string> = { [SKILL_ENTRYPOINT]: "" };

function movedFiles(
  files: Record<string, string>,
  from: string,
  to: string
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(files).map(([path, content]) => [movedPath(path, from, to) ?? path, content])
  );
}

/** Files for a skill not created yet. A new `sessionKey` starts from a blank SKILL.md. */
export function useLocalSkillFileStore(sessionKey: string): SkillFileStore {
  const [session, setSession] = useState({ sessionKey, files: INITIAL_LOCAL_FILES });
  if (session.sessionKey !== sessionKey) {
    setSession({ sessionKey, files: INITIAL_LOCAL_FILES });
  }
  const files = session.sessionKey === sessionKey ? session.files : INITIAL_LOCAL_FILES;
  // Writes land here at once so back-to-back edits see each other before a re-render.
  const filesRef = useRef(files);
  filesRef.current = files;

  return useMemo((): SkillFileStore => {
    const commit = (next: Record<string, string>) => {
      filesRef.current = next;
      setSession({ sessionKey, files: next });
    };
    return {
      loading: false,
      paths: Object.keys(files),
      read: (path) => filesRef.current[path] ?? "",
      write: async (path, content, { create }) => {
        const normalized = prepareDraftSkillFile({
          path,
          content,
          files: filesRef.current,
          rejectExisting: create,
        });
        commit({ ...filesRef.current, [normalized]: content });
      },
      remove: async (path) => {
        commit(Object.fromEntries(Object.entries(filesRef.current).filter(([p]) => p !== path)));
      },
      move: async (from, to) => commit(movedFiles(filesRef.current, from, to)),
      savedMessage: "Saved. Files upload when you create the skill",
      localFiles: files,
    };
  }, [files, sessionKey]);
}

/** Files of a saved skill, read and written through the API. */
export function useServerSkillFileStore(params: {
  skillId: string | null;
  tenantId: string | undefined;
}): SkillFileStore {
  const { skillId, tenantId } = params;
  const client = useApolloClient();
  const { data, loading, refetch } = useQuery(SKILL_FILES_QUERY, {
    variables: { input: { id: skillId ?? "", tenantId: tenantId ?? "" } },
    skip: !skillId || !tenantId,
    fetchPolicy: "network-only",
  });
  const [writeFile] = useMutation(WRITE_SKILL_FILE_MUTATION);
  const [deleteFile] = useMutation(DELETE_SKILL_FILE_MUTATION);

  return useMemo((): SkillFileStore => {
    const paths = (data?.skillFiles ?? []).map((file) => file.path);
    const input = (path: string) => ({ id: skillId ?? "", tenantId: tenantId ?? "", path });
    const read = async (path: string) => {
      // Each open is its own request; the editor drops answers it no longer wants.
      const result = await client.query({
        query: SKILL_FILE_CONTENT_QUERY,
        variables: { input: input(path) },
        fetchPolicy: "network-only",
      });
      return result.data.skillFileContent;
    };
    const write = async (path: string, content: string) => {
      await writeFile({ variables: { input: { ...input(path), content } } });
    };
    const remove = async (path: string) => {
      await deleteFile({ variables: { input: input(path) } });
    };

    return {
      loading,
      paths,
      read,
      write: async (path, content, { create }) => {
        await write(path, content);
        if (create) {
          await refetch();
        }
      },
      remove: async (path) => {
        await remove(path);
        await refetch();
      },
      // A copy then a delete per file, every copy landing before any delete.
      // The tree is refetched whatever happens, so a partial move shows as it is.
      move: async (from, to) => {
        try {
          const moves = paths.flatMap((path) => {
            const next = movedPath(path, from, to);
            return next ? [{ from: path, to: next }] : [];
          });
          for (const move of moves) {
            await write(move.to, await read(move.from));
          }
          for (const move of moves) {
            await remove(move.from);
          }
        } finally {
          // Don't let a failed refetch hide why the move failed.
          await refetch().catch(() => undefined);
        }
      },
      savedMessage: "File saved",
      localFiles: null,
    };
  }, [client, data?.skillFiles, deleteFile, loading, refetch, skillId, tenantId, writeFile]);
}
